import type { JSX, ReactNode } from 'react';

import { Dialog } from './Dialog';
import { useTelefon } from './telefon';

/**
 * DIE BEARBEITUNG EINER TABELLENZEILE AUF DEM TELEFON (Auftrag CRM-106, ÄA-63).
 *
 * Am Desktop klappt ein Formular als eigene Zeile unter der Zeile auf, die es
 * betrifft — dort ist es im Blick, und die Spalten darüber stehen daneben.
 * Auf dem Telefon rollt dieselbe Tabelle waagerecht in ihrer Hülle: Das
 * Formular stünde dann in einer Zeile, die breiter ist als der Schirm, und
 * die Hälfte der Felder läge außerhalb. Dort öffnet es deshalb im Dialog, der
 * auf dem Telefon den ganzen Schirm nimmt (CRM-105).
 *
 * Über 600 px ist der Aufbau Zeichen für Zeichen der alte; nur die Weiche
 * `useTelefon` entscheidet, und in den Prüfungen (jsdom) gilt der breite Schirm.
 */

type Fenster = {
  /** Titel des Dialogs auf dem Telefon; am Desktop trägt das Formular seinen eigenen. */
  readonly titel: string;
  readonly unterzeile?: string | undefined;
  /** Was Esc und das Schließkreuz tun — in der Regel dasselbe wie „Abbrechen". */
  readonly beiSchliessen: () => void;
  readonly children: ReactNode;
};

/** Auf dem Telefon im Dialog, sonst an Ort und Stelle. */
export function Telefonfenster({ titel, unterzeile, beiSchliessen, children }: Fenster): JSX.Element {
  const telefon = useTelefon();
  if (!telefon) return <>{children}</>;
  return (
    <Dialog titel={titel} unterzeile={unterzeile} beiSchliessen={beiSchliessen}>
      {children}
    </Dialog>
  );
}

/**
 * Die aufgeklappte Formularzeile einer Tabelle. Am Desktop `<tr><td colSpan>`,
 * wie bisher; auf dem Telefon bleibt die Zeile als leerer Anker stehen (eine
 * `<div>` darf nicht in `<tbody>`), und das Formular steht im Dialog.
 */
export function Zeilenformular({
  spalten,
  ...fenster
}: Fenster & { readonly spalten: number }): JSX.Element {
  const telefon = useTelefon();
  return (
    <tr className={telefon ? 'zeilenformular--telefon' : undefined}>
      <td colSpan={spalten}>
        <Telefonfenster {...fenster} />
      </td>
    </tr>
  );
}
